import React, { useState, useEffect } from 'react';
import { Modal, Button, Tabs, Badge } from 'antd';
import api from '../functions/api';
import getUserId from '../functions/getUserId';
import PF_Alerts from './PF_Alerts';
import FavoritesAlerts from './FavoritesAlerts';
import Market_Alerts from './Market_Alerts';
import Opportunities from './Opportunities'; // Import the new component
import Swaps from './Swaps'; // DAS-2360: swap-of-the-day tab
import BroadPictureAlerts from './BroadPictureAlerts'; // DAS-2681: top-down exposure warnings
import LLMAdviceTab from './LLMAdviceTab'; // DAS-2897: LLM Portfolio Analysis advice
import StrategyAlertTab from './StrategyAlertTab'; // DAS-3341: strategy review lights + life events
import CapitalFitBanner from './CapitalFitBanner'; // DAS-2364: header banner for per-part cash + settings
import { fetchSwapOfTheDay } from '../functions/alertClauseService'; // DAS-2360
import { useSelector } from 'react-redux';
import { Bell as Icon } from "lucide-react";
import NavButton from "./ui/NavButton";
import MarketWeatherBadge from './MarketWeatherBadge'; // DAS-2765
import PanicBanner from './PanicBanner'; // DAS-2768
import Box from "@mui/material/Box";

const { TabPane } = Tabs;

const Alerts = ({ disabled }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [lastAlertGeneration, setLastAlertGeneration] = useState(null);
  const opportunitiesUnseen = useSelector(state => 
  state?.portfolio?.data?.opportunities_alerts?.some(alert => alert.seen === 0)
);

  // const [opportunitiesUnseen, setOpportunitiesUnseen] = useState(false); // New state for Opportunities alerts
  const marketAlerts = useSelector(state => state?.portfolio?.data?.market_alerts || []);
  const marketTabDisabled = !Array.isArray(marketAlerts) || marketAlerts.length === 0;

  // Fetch last alert generation time
  useEffect(() => {
    const fetchLastAlertGeneration = async () => {
      try {
        // Perf: light mode — we only need one script's last-run epoch, so skip the
        // heavy cron_check() filesystem scan in /script_reporting (was ~3.7s cold).
        const response = await api.get('/script_reporting?light=1');
        const { database_results } = response.data;

        // Find the record for DR_Alert_daily_scripts_optimized.py
        const alertScript = database_results.find(
          record => record.script_name === 'DR_Alert_daily_scripts_optimized.py'
        );
        
        if (alertScript && alertScript.epoch) {
          setLastAlertGeneration(new Date(alertScript.epoch * 1000));
        }
      } catch (error) {
        console.error('Error fetching script reporting data:', error);
      }
    };

    fetchLastAlertGeneration();
  }, []);

  const showModal = () => {
    setIsVisible(true);
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  // Check for unseen alerts in Redux
  const portfolioUnseen = useSelector(state => state.portfolio?.data?.pf_alert?.some(alert => alert.seen === 0));
  const favoritesUnseen = useSelector(state => state.favorites?.data?.favorites_alert_data?.some(alert => alert.seen === 0));
  const userId = getUserId();
  const marketUnseen = useSelector(state =>
    state?.portfolio?.data?.market_alerts?.some(alert =>
      !alert.seenby ||
      !alert.seenby
        .split(',')
        .map(id => id.trim())
        .includes(userId ? userId.toString() : "")
    )
  );

  // DAS-2360: swap-of-the-day unseen state.
  // The swap row isn't in Redux (yet) — fetched lazily on mount via the API.
  // Badge dot = row exists today AND user hasn't seen it AND it's a real pair
  // (no-pair days have no actionable content, so no badge).
  const [swapsUnseen, setSwapsUnseen] = useState(false);
  // DAS-2897/2898 owner request: unseen dots for the LLM Advice and Broad
  // Picture tabs, same lazily-fetched pattern as the Swaps dot below.
  const [llmAdviceUnseen, setLlmAdviceUnseen] = useState(false);
  const [broadPictureUnseen, setBroadPictureUnseen] = useState(false);
  const [strategyUnseen, setStrategyUnseen] = useState(false); // DAS-3341
  const [activeTab, setActiveTab] = useState('1');
  const [jumpAlertId, setJumpAlertId] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const row = await fetchSwapOfTheDay();
      if (cancelled) return;
      const seen = row?.seen === 1 || row?.seen === true;
      const hasPair = !!(row?.sell_ticker && row?.buy_ticker);
      setSwapsUnseen(hasPair && !seen);
    })();
    (async () => {
      try {
        const res = await api.get(`/llm_advice_list/${userId}`);
        if (!cancelled) setLlmAdviceUnseen((res.data?.advice || []).some(a => !a.seen));
      } catch (e) { /* no dot on fetch failure */ }
    })();
    (async () => {
      try {
        const res = await api.get(`/broad_picture_alerts/${userId}`);
        if (!cancelled) setBroadPictureUnseen((res.data?.alerts || []).some(a => !a.seen));
      } catch (e) { /* no dot on fetch failure */ }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Function to update unseen alerts from Opportunities (passed as prop)
  // const updateOpportunitiesUnseen = (unseen) => {
  //   setOpportunitiesUnseen(unseen);
  // };

  // Final check for any unseen alerts in all sources
  const hasUnseenAlerts = portfolioUnseen || favoritesUnseen || marketUnseen || opportunitiesUnseen || swapsUnseen || llmAdviceUnseen || broadPictureUnseen || strategyUnseen;

  return (
    <>
      <Badge dot={hasUnseenAlerts} color="red" data-testid="alerts-open-button">
        <NavButton
          icon={<Icon size={14} />}
          label="Alerts"
          onClick={showModal}
          disabled={disabled}
          tooltip="Alerts on Portfolio, Favorites, Market and Opportunities"
        />
      </Badge>
      <Modal
        title={
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span>Alerts</span>
              {/* DAS-2765: daily market weather flag */}
              <MarketWeatherBadge />
            </div>
            {lastAlertGeneration && (
              <div style={{ 
                fontSize: '12px', 
                fontStyle: 'italic', 
                color: '#666',
                marginTop: '4px'
              }}>
                last alert generation ended {lastAlertGeneration.toLocaleString()}
              </div>
            )}
          </div>
        }
        open={isVisible}
        onCancel={handleClose}
        width={1400}
        footer={[
          <Button key="ok" type="primary" onClick={handleClose}>
            OK
          </Button>,
        ]}
      >
        {/* DAS-2768: cooling-off banner, renders only while market weather = PANIC */}
        <PanicBanner />

        {/* DAS-2364: aggregate capital-fit banner at top of the modal */}
        <CapitalFitBanner />

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            tab={<Badge dot={portfolioUnseen} color="red">Portfolio Alerts</Badge>} 
            key="1"
          >
            <PF_Alerts jumpAlertId={jumpAlertId} onJumpConsumed={() => setJumpAlertId(null)} />
          </TabPane>
          <TabPane 
            tab={<Badge dot={favoritesUnseen} color="red">Favorites Alerts</Badge>} 
            key="2"
          >
            <FavoritesAlerts />
          </TabPane>
          <TabPane 
            tab={<Badge dot={opportunitiesUnseen} color="red">Opportunities Alerts</Badge>} 
            key="3"
          >
            <Opportunities />
            {/* <Opportunities updateUnseen={updateOpportunitiesUnseen} /> */}
          </TabPane>
          <TabPane
  tab={
    <span
      data-testid="market-alerts-tab-label"
      style={
        marketTabDisabled
          ? { color: '#bbb', cursor: 'not-allowed', opacity: 0.7 }
          : {}
      }
    >
      <Badge dot={marketUnseen} color="red">
        Market Alerts
      </Badge>
    </span>
  }
  key="4"
  disabled={marketTabDisabled}
>
  <Market_Alerts />
</TabPane>

          {/* DAS-2360: swap-of-the-day tab. Badge dot fires when today's row
              has a real pair and seen != 1. Opening the tab triggers Swaps.jsx's
              auto-mark-seen useEffect; the badge here clears on next reload. */}
          <TabPane
            tab={<Badge dot={swapsUnseen} color="red">Swaps</Badge>}
            key="5"
          >
            <Swaps onUnseenChange={setSwapsUnseen} />
          </TabPane>

          {/* DAS-2681: top-down exposure warnings (scope Broad_Picture) — own
              lifecycle (created/retired by the daily Broad Picture run), so a
              separate tab instead of mixing into Portfolio Alerts. */}
          <TabPane tab={<Badge dot={llmAdviceUnseen} color="red" data-testid="llm-advice-tab-label">LLM Advice</Badge>} key="7">
            <LLMAdviceTab onUnseenChange={setLlmAdviceUnseen} />
          </TabPane>
          <TabPane tab={<span data-testid="broad-picture-tab-label"><Badge dot={broadPictureUnseen} color="red">Broad Picture</Badge></span>} key="6">
            <BroadPictureAlerts onUnseenChange={setBroadPictureUnseen} />
          </TabPane>

          {/* DAS-3341: strategy review lights (DAS-3331/DAS-3340) + life events (DAS-3338) */}
          <TabPane
            tab={<span data-testid="strategy-alert-tab-label"><Badge dot={strategyUnseen} color="red">Strategy Alert</Badge></span>}
            key="8"
          >
            <StrategyAlertTab onUnseenChange={setStrategyUnseen} />
          </TabPane>

        </Tabs>
      </Modal>
    </>
  );
};

export default Alerts;
